import {
	AfterViewInit,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation
} from '@angular/core';
import { FileUploadServiceProvider } from '@services/files/file-upload-service/file-upload.service';
import { FileUploadDataProvider } from '@services/files/file-upload.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { Subject } from 'rxjs/Subject';
import Counter from '@components/common/sn-quill-editor/counter';
import { COLOR_PICKER_OPTIONS, quillOptions } from '@models/models';
import { Range } from 'ngx-quill';
import { ImageDrop } from '@components/common/sn-quill-editor/image-drop';
import { modifyAlignIconsSet } from '@components/common/sn-quill-editor/quill-editor.models';
import ImageResize from 'quill-image-resize';
import { Toolbar } from './Toolbar';

import * as QuillNamespace from 'quill';
import { variablesList } from '@models/system-variables/variables-to-insert';

const Quill = QuillNamespace;

const AlignStyle = Quill.import('attributors/style/align');
const SizeStyle = Quill.import('attributors/style/size');
const FontStyle = Quill.import('attributors/style/font');

Quill.register('modules/counter', Counter);
Quill.register(AlignStyle, true);
Quill.register(FontStyle, true);
const Icons = Quill.import('ui/icons');

Icons.align = modifyAlignIconsSet();

SizeStyle.whitelist = ['10px', '18px', '28px'];
Quill.register(SizeStyle, true);

Quill.register('modules/imageDrop', ImageDrop);
Quill.register('modules/imageResize', ImageResize);
Quill.register(Icons, true);

const linkVariables = [
	'%contract_link%',
	'%invoice_link%',
	'%quote_link%',
	'%questionnaire_link%',
	'%galleries_link%'
];

@Component({
	selector: '',
	template: '',
	encapsulation: ViewEncapsulation.None
})
export class SnQuillEditorComponent
	implements OnInit, AfterViewInit, OnDestroy {
	@Input('varsList') varsList = 'JOB_MENU';
	@Input('filesObj') filesObj: any;
	@Input('initValue') initValue = '';
	@Input('maxLength') maxLength = 5000;
	@Input('placeholder') placeholder = 'Type text here … ';
	@Input('editorID') editorID = 'default';
	@Input('styles') styles: any = { minHeight: '250px' };
	/* Possible values: variables attachments counter color size font image */
	@Input('toolbarSettings') toolbarSettings = 'DEFAULT';
	@Input() imageLoadHandler: any;
	@Output() onTextChange: EventEmitter<any> = new EventEmitter<any>();
	@Output() onTextLengthChange: EventEmitter<any> = new EventEmitter<any>();

	quill: any;
	isStoredFiles: boolean;
	contentLength: number;
	currentSelection: Range;
	counterShow = false;
	attachmentSelector: string;
	editor: Element = document.querySelector('.ql-editor');
	tooltip: Element;
	toolbar: Element;

	variablesToInsert: Array<any>;
	unsubscribe: Subject<void> = new Subject<void>();
	quillEditorOptions: quillOptions;
	attachmentsOption: Array<any>;
	imageDropErrorEvent;
	/* Attention
   	 * if font size is will be change also change this in the scss file
   	 */
	private fontSizeOption: Array<any> = [
		{ size: ['10px', false, '18px', '28px'] }
	];
	private fontOption: Array<any> = [{ font: [] }];

	private fontColor: Array<any> = [{ color: COLOR_PICKER_OPTIONS }];

	quillDefaultOptions: quillOptions = {
		toolbar: {
			container: [
				['bold', 'italic', 'underline'],
				[],
				['link'],
				[{ list: 'bullet' }, { list: 'ordered' }],
				[{ align: [] }],
				[],
				[],
				[]
			],
			handlers: {
				link: (value: boolean): void => this.linkHandler(value)
			}
		},
		clipboard: {
			matchVisual: false
		},
		imageDrop: {},
		imageResize: false
	};

	constructor(
		private filesDataService: FileUploadDataProvider,
		private filesService: FileUploadServiceProvider,
		private messageService: MessageService
	) {}

	ngOnInit() {
		this.quillEditorOptions = this.setEditorOptions();
	}

	setEditorOptions() {
		if (this.toolbarSettings === 'DEFAULT') {
			return this.quillDefaultOptions;
		}

		const toolbarOptionsArr = this.toolbarSettings.split(' ');
		toolbarOptionsArr.forEach((option: string): void => {
			if (option === 'color') {
				this.quillDefaultOptions.toolbar.container[1].push(...this.fontColor);
				this.quillDefaultOptions.toolbar.handlers.color = this.colorHandler.bind(
					this
				);
			}
			if (option === 'image') {
				this.quillDefaultOptions.toolbar.container[2].push('image');
				this.quillDefaultOptions.toolbar.handlers.image = this.imageHandler.bind(
					this
				);
				this.quillDefaultOptions.imageDrop = {
					images: true
				};
				this.quillDefaultOptions.imageResize = {
					modules: ['Resize', 'DisplaySize']
				};
			}
			if (option === 'imageLoad') {
				this.quillDefaultOptions.toolbar.container[2].push('image');
				this.quillDefaultOptions.toolbar.handlers.image = this.imageLoadHandle.bind(
					this
				);
				this.quillDefaultOptions.imageDrop = {
					loadImage: value => {
						const formData = new FormData();
						formData.append('file', value);
						this.imageLoadHandler(formData, this.insertImage.bind(this));
					},
					images: true
				};
				this.quillDefaultOptions.imageResize = {
					modules: ['Resize', 'DisplaySize', Toolbar]
				};
			}
			if (option === 'font') {
				this.quillDefaultOptions.toolbar.container[5].push(...this.fontOption);
			}
			if (option === 'size') {
				this.quillDefaultOptions.toolbar.container[6].push(
					...this.fontSizeOption
				);
				this.quillDefaultOptions.toolbar.handlers.size = this.sizeHandler.bind(
					this
				);
			}
			if (option === 'variables') {
				this.quillDefaultOptions.toolbar.container[7].push(
					...[{ variables: [...this.addVariables()] }]
				);
				this.quillDefaultOptions.toolbar.handlers.variables = this.variablesHandler.bind(
					this
				);
			}
			if (option === 'attachments') {
				// Put option in specific position
				this.quillDefaultOptions.toolbar.container[2].push(
					...this.attachmentsOption
				);
				this.quillDefaultOptions.toolbar.handlers.attachments = this.attachmentsHandler.bind(
					this
				);
			}
			if (option === 'counter') {
				this.counterShow = true;
				this.quillDefaultOptions.counter = {
					container: `counter-${this.editorID}`,
					maxLength: this.maxLength,
					currentLength: val => this.onTextLengthChange.emit(val)
				};
			}
		});

		return this.quillDefaultOptions;
	}

	onEditorCreated(ev): void {
		this.quill = ev;
		this.editor = this.quill.root;
		this.toolbar = this.editor.parentElement.parentElement.children[0];
		this.getAttachments();
		this.checkJobFiles();

		this.addCustomMatchers();
		this.listenToImageDropErrors();
	}

	ngAfterViewInit(): void {
		this.attachClickForEditLink();
	}

	ngOnDestroy(): void {
		this.filesDataService.setCheckedFiles(null);
		this.unsubscribe.complete();
		this.detachImageDropErrorHandler();
	}

	/* Emiters */
	// emitNewAttachments($event): void {
	// 	this.onAttachmentsChange.emit($event);
	// }

	onContentChange(value): void {
		if (value.source === 'user') {
			this.onTextChange.emit(value.html ? value.html.normalize() : value.html);
		}
	}

	onSelectionChanged(value): void {
		this.currentSelection = value;
	}

	/* Variables */
	private addVariables(): Array<string> {
		this.variablesToInsert = variablesList[this.varsList];

		return Object.keys(variablesList[this.varsList]).map(
			(key: string): string => key
		);
	}

	/* Attachments */
	protected attachFileFromComputer = (): void => {
		const input: HTMLElement = document.querySelector(
			this.attachmentSelector
		) as HTMLElement;
		input.click();
	};

	protected disabledAttachmentsFromFiles() {
		let openDropDown = false;
		const attachmentsToolbarBtn = this.toolbar.querySelector('.ql-attachments');

		if (attachmentsToolbarBtn === null) {
			return;
		}

		attachmentsToolbarBtn.addEventListener('click', () => {
			openDropDown = !openDropDown;
			if (openDropDown) {
				const fromFilesOption = this.toolbar.querySelector(
					'.ql-picker-item[data-value="From Files"]'
				);
				if (!fromFilesOption.classList.contains('disableOption')) {
					fromFilesOption.classList.add('disableOption');
				}
			}
		});
	}

	getAttachments() {
		this.filesDataService.checkedFilesObservable
			.takeUntil(this.unsubscribe)
			.subscribe(
				(chosenFiles: Array<any>) => this.succesGetAttachments(chosenFiles),
				() => this.messageService.showToaster('e_0', 'general')
			);
	}

	private succesGetAttachments(chosenFiles: Array<any>) {
		if ((this.editor as HTMLElement).getBoundingClientRect().width < 40) {
			return;
		}
		if (chosenFiles !== null) {
			if (this.filesObj === undefined) {
				this.filesObj = {
					attachments: []
				};
			}
			const filesToAdd = [];
			chosenFiles.forEach(chosenFile => {
				if (this.checkIfFileExist(chosenFile) === -1) {
					filesToAdd.push(chosenFile);
				}
			});
			const newAttachmentsSize = this.filesService.getAllAttachmentsSize(
				this.filesObj.filesSize,
				filesToAdd
			);

			const maxSize = this.filesService.maxAttachment;
			if (newAttachmentsSize > maxSize) {
				this.messageService.showToaster('w_0', 'attachments');
				return;
			}

			this.filesObj.filesSize = newAttachmentsSize;
			filesToAdd.forEach(chosenFile => {
				this.filesObj.attachments.push(chosenFile);
			});
		}
	}

	private checkIfFileExist(chosenFile) {
		return this.filesObj.attachments.findIndex(
			file => file.id === chosenFile.id
		);
	}

	checkJobFiles() {
		this.filesDataService.filesObservable
			.takeUntil(this.unsubscribe)
			.subscribe((storedFiles: Array<any>) => {
				this.isStoredFiles = !!storedFiles.length;

				if (!this.isStoredFiles) {
					this.disabledAttachmentsFromFiles();
				}
			});
	}

	/* Link */
	private linkHandler(isNew: boolean): void {
		const cursorPosition = this.getCursorPosition();
		let linkData;

		// Edit or create link
		!isNew
			? (linkData = this.prepareLinkEdit(cursorPosition))
			: (linkData = this.prepareCreateDataIfNeeded(cursorPosition));

		this.openLinkModal({
			url: linkData.url,
			text: linkData.text
		});
	}

	// next methods are overwritten in child classes
	/* Attachments */
	showChooseFileModal() {}
	openLinkModal(delta?): void {}
	attachmentsHandler(type: string): void {}

	prepareLinkHtml(linkData) {
		if (linkData.isEmail) {
			linkData.url = `mailto:${linkData.url}`;
		}
		return {
			html: `
      <span>
        <a href="${linkData.url}" >${linkData.text}</a>
      </span>
     `,
			length: linkData.text.length
		};
	}

	insertIntoQuill(data): void {
		const valueToInsert = data.html;
		const cursorPosition = this.getCursorPosition();
		this.quill.clipboard.dangerouslyPasteHTML(
			cursorPosition,
			valueToInsert,
			'user'
		);
		this.setCursorPosition(data.length, cursorPosition);
	}

	insertImage(data) {
		const valueToInsert = data.html;
		const cursorPosition = this.getCursorPosition();
		const imageWrap = this.editor.getElementsByTagName('img')[0];
		if (imageWrap) {
			const reduntP = imageWrap.closest('p');
			if (reduntP) {
				reduntP.remove();
			}
		}
		this.quill.clipboard.dangerouslyPasteHTML(
			cursorPosition - 1 || 0,
			valueToInsert,
			'user'
		);
		this.setCursorPosition(data.length, cursorPosition);
	}

	updateLink(delta, data): void {
		// If we need to create a link from selected text
		if (!delta.url) {
			const cursorPos = this.getCursorPosition();
			this.quill.updateContents(
				[
					{ retain: cursorPos },
					{ delete: data.text.length },
					{ insert: '', attributes: { link: data.url } }
				],
				'user'
			);
			this.setCursorPosition(0, cursorPos);
		} else {
			const HTMLCollection = this.editor.querySelectorAll('a');
			const links = Array.from(HTMLCollection);

			for (let i = 0; i < links.length; i++) {
				let href = links[i].getAttribute('href');
				const text = links[i].innerText;
				const isEmail = href.includes('mailto:');

				if (isEmail) {
					href = href.split(':')[1];
				}

				if (href === delta.url && text.includes(delta.text)) {
					if (isEmail) {
						data.url = `mailto:${data.url}`;
					}
					HTMLCollection[i].href = data.url;
					HTMLCollection[i].innerHTML = data.text;
				}
			}
			setTimeout(() => {
				const selectedText = this.quill.getContents(this.getCursorPosition());
				this.setCursorPosition(
					selectedText.ops[0].insert.length,
					this.getCursorPosition()
				);
			}, 0);
		}
	}

	private getFullLinkName(delta): string {
		const links = Array.from(this.editor.querySelectorAll('a'));

		for (let i = 0; i < links.length; i++) {
			let href = links[i].getAttribute('href');
			if (href.includes('mailto:')) {
				href = href.split(':')[1];
			}
			const text = links[i].innerHTML.replace(/<\/?[^>]+(>|$)/g, '');
			// If we found link with exact text and href attribute
			if (href === delta.attributes.link && text.includes(delta.insert)) {
				return text;
			}
		}
		return '';
	}

	private attachClickForEditLink(): void {
		const tooltips = this.editor.parentElement.querySelectorAll('.ql-tooltip');
		this.tooltip = tooltips[tooltips.length - 1];
		const element = this.tooltip.querySelector('.ql-action');
		element.addEventListener(
			'click',
			event => {
				this.linkHandler(false);
			},
			false
		);
		element.innerHTML = `<span class="editLinkTips">
         <i class="fi fi-edit link-icon" aria-hidden="true"></i>
         Edit link
       </span>`;
	}

	/* Handlers */
	private variablesHandler(variable: string): void {
		if (variable) {
			const valueToInsert = this.variablesToInsert[variable];
			const cursorPosition = this.getCursorPosition();
			this.quill.clipboard.dangerouslyPasteHTML(
				cursorPosition,
				`${valueToInsert} &nbsp;`,
				'user'
			);
			this.setCursorPosition(
				valueToInsert.replace(/<\/?[^>]+(>|$)/g, '').length + 1,
				cursorPosition
			);
		}
	}

	getCursorPosition(): number {
		// Get cursor position
		let cursorPosition = this.quill.getSelection();
		// Get fallback cursor position if we do not have active focus on editor
		if (!cursorPosition) {
			cursorPosition = this.quill.selection.savedRange.index;
		} else {
			cursorPosition = cursorPosition.index;
		}
		return cursorPosition;
	}

	protected setCursorPosition(length: number, cursorPosition?: number): void {
		setTimeout(() => this.quill.setSelection(cursorPosition + length), 0);
	}

	prepareCreateDataIfNeeded(cursorPosition?) {
		const selection = this.quill.getSelection();
		const linkData = this.quill.getContents(cursorPosition).ops[0];
		let url = null;
		let text = null;

		// Track if we have selected text to pass (not link) during creation
		if (selection.length !== 0) {
			text = this.quill.getContents(selection.index, selection.length).ops[0]
				.insert;
		}

		// Important! Some devices did not handle Quill.js edit events correctly, so we need to catch edit while editor want's to create new link
		if (linkData.attributes && linkData.attributes.link) {
			const data = this.prepareLinkEdit(cursorPosition);
			url = data.url;
			text = data.text;
		}
		return {
			url,
			text
		};
	}

	prepareLinkEdit(cursorPosition: number) {
		let linkData = this.quill.getContents(cursorPosition).ops[0];
		// hide default tooltip
		this.tooltip.classList.add('removeTooltip');

		// Handle case when cursor situated right after the link
		if (
			!linkData.attributes ||
			(linkData.attributes && !linkData.attributes.link)
		) {
			linkData = this.quill.getContents(cursorPosition - 1).ops[0];
		}

		// Remove mail protocol from selected href
		if (linkData.attributes.link.includes('mailto:')) {
			linkData.attributes.link = linkData.attributes.link.split(':')[1];
		}

		// Required separate get name handler, because of incorrect behaviour of quill text selection by cursor index
		const linkText = this.getFullLinkName(linkData);

		return { url: linkData.attributes.link, text: linkText };
	}

	private colorHandler(color: string): void {
		if (this.currentSelection.index) {
			const data = this.quill.getContents(
				this.currentSelection.index,
				this.currentSelection.length || 1
			).ops[0];
			if (data && data.attributes && data.attributes.link) {
				return;
			}
		}

		this.quill.format('color', color, 'user');
	}

	private sizeHandler(value: string): void {
		this.quill.format('size', value, 'user');

		// Setting !important manually to support different email protocols
		setTimeout(() => {
			this.setImportantToTextSize();
		}, 0);
	}

	private setImportantToTextSize(): void {
		const elementsDOM = this.editor.querySelectorAll('*[style*="font-size"]');
		const elems = Array.from(elementsDOM);
		elems.map((el, i) => {
			if (!el.style.fontSize.includes('!important')) {
				let styleStr = '';
				if (el.style.fontSize) {
					styleStr += `font-size:${el.style.fontSize} !important;`;
				}
				if (el.style.color) {
					styleStr += `color: ${el.style.color};`;
				}
				if (el.style.fontFamily) {
					styleStr += `font-family: ${el.style.fontFamily};`;
				}
				el.setAttribute('style', styleStr);
			}
		});
		this.quill.update('user');
	}

	/* Custom matchers */

	private addCustomMatchers(): void {
		this.removeBlackTextStyle();
		this.removeImgHeight();
		this.clearLinks();
		this.normalizeUnicode();
	}

	private removeBlackTextStyle(): void {
		const selector = 'span[style*="color: rgb(0, 0, 0)"]';
		this.quill.clipboard.addMatcher(selector, (node, delta) => {
			delta.ops = delta.ops.map(op => ({
				insert: op.insert
			}));
			return delta;
		});
	}

	private removeImgHeight() {
		this.quill.clipboard.addMatcher('IMG', (node, delta) => {
			delta.ops = delta.ops.map(op => ({
				...op,
				attributes: {
					...(op.attributes || {}),
					height: null,
					margin: null
				}
			}));
			return delta;
		});
	}

	private clearLinks() {
		this.quill.clipboard.addMatcher('A', (node, delta) => {
			delta.ops = delta.ops.map(op => {
				let link = op.attributes.link;
				if (!link) return op;
				linkVariables.forEach(linkVar => {
					const startIndex = link.indexOf(linkVar);
					if (startIndex !== -1) {
						link = link.slice(startIndex, startIndex + linkVar.length);
					}
				});
				return {
					...op,
					attributes: {
						...(op.attributes || {}),
						link: link
					}
				}
			});
			return delta;
		});
	}

	private normalizeUnicode() {
		const selector = '*';
		this.quill.clipboard.addMatcher(selector, (node, delta) => {
			delta.ops = delta.ops.map(op => {
				return {
					...op,
					insert: typeof op.insert === 'string' ? op.insert.normalize() : op.insert
				}
			});
			return delta;
		});
	}

	private imageLoadHandle(state: boolean): void {
		if (state) {
			const input = <HTMLInputElement>(
				document.getElementById(`image-input-${this.editorID}`)
			);
			input.onchange = (): void => {
				const file: File = input.files[0];
				// file type is only image.
				if (file.type.match(/^image\/(jpe?g|png|bmp\.microsoft\.icon)/i)) {
					const maxFileSize = 2 * 1000 * 1000;
					if (file.size > maxFileSize) {
						this.messageService.showToaster('e_1', 'companyInformation', '2');
					} else {
						const newData: any = new FormData();
						newData.append('file', file);
						this.imageLoadHandler(newData, this.insertImage.bind(this));
					}
				} else {
					this.messageService.showToaster('e_0', 'companyInformation');
				}
				input.value = null;
			};
			input.click();
		}
	}

	private imageHandler(state: boolean): void {
		if (state) {
			const input = <HTMLInputElement>(
				document.getElementById(`image-input-${this.editorID}`)
			);
			input.onchange = (): void => {
				const file: File = input.files[0];
				// file type is only image.
				if (file.type.match(/^image\/(jpe?g|png|bmp\.microsoft\.icon)/i)) {
					const maxFileSize = 2 * 1000 * 1000;
					if (file.size > maxFileSize) {
						this.messageService.showToaster('e_1', 'companyInformation', '2');
					} else {
						const reader: FileReader = new FileReader();
						reader.onload = (): void => {
							const range = this.quill.getSelection();
							const img = `<img src="${reader.result}" alt="${file.name}"/>`;
							this.quill.clipboard.dangerouslyPasteHTML(
								range.index,
								img,
								'user'
							);
						};
						reader.readAsDataURL(file);
					}
				} else {
					this.messageService.showToaster('e_0', 'companyInformation');
				}
				input.value = null;
			};
			input.click();
		}
	}

	private listenToImageDropErrors(): void {
		this.imageDropErrorEvent = document.addEventListener(
			'ImageDropError',
			(ev: CustomEvent): void => {
				if (ev.detail.status) {
					switch (ev.detail.status) {
						case 'MULTIPLE':
							this.messageService.showToaster('e_4', 'companyInformation');
							break;
						case 'TYPE':
							this.messageService.showToaster('e_0', 'companyInformation');
							break;
						case 'SIZE':
							this.messageService.showToaster('e_1', 'companyInformation', '2');
							break;
						default:
							break;
					}
				}
			},
			false
		);
	}

	private detachImageDropErrorHandler(): void {
		if (this.imageDropErrorEvent) {
			this.imageDropErrorEvent();
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./shared/components/common/sn-quill-editor/sn-quill-editor.ts